import { RISK_LEVEL_COLORS, sortLabelSx } from "@app/theme/tokens";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { FleetPlant } from "../types";
import { mw } from "../utils/format";
import DashboardCard from "./DashboardCard";
import { PendingCardBody, PendingValue } from "./PendingValue";

type SortKey = keyof Pick<
  FleetPlant,
  | "name"
  | "capacity_mw"
  | "spof_count"
  | "severe_count"
  | "high_count"
  | "medium_count"
  | "low_count"
  | "exposed_mw"
>;

const COLUMNS: { key: SortKey; label: string; color?: string }[] = [
  { key: "name", label: "Plant" },
  { key: "capacity_mw", label: "Capacity MW" },
  { key: "spof_count", label: "SPOFs" },
  { key: "severe_count", label: "Severe", color: RISK_LEVEL_COLORS.Severe },
  { key: "high_count", label: "High", color: RISK_LEVEL_COLORS.High },
  { key: "medium_count", label: "Medium", color: RISK_LEVEL_COLORS.Medium },
  { key: "low_count", label: "Low", color: RISK_LEVEL_COLORS.Low },
  { key: "exposed_mw", label: "Exposed MW" },
];

function compare(a: FleetPlant, b: FleetPlant, key: SortKey) {
  const left = a[key];
  const right = b[key];
  if (typeof left === "string" || typeof right === "string") {
    return String(left).localeCompare(String(right));
  }
  return (left ?? -1) - (right ?? -1);
}

export default function PlantRiskTable({ plants }: { plants: FleetPlant[] }) {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>("exposed_mw");
  const [descending, setDescending] = useState(true);

  const sorted = [...plants].sort(
    (a, b) => compare(a, b, sortKey) * (descending ? -1 : 1),
  );

  const sortBy = (key: SortKey) => {
    setDescending(key === sortKey ? !descending : true);
    setSortKey(key);
  };

  return (
    <DashboardCard title="Plant SPOF Risk Summary">
      {plants.length === 0 ? (
        <PendingCardBody hint="No power plant has been created yet." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column, index) => (
                <TableCell
                  key={column.key}
                  align={index === 0 ? "left" : "right"}
                  sx={{ color: column.color, fontWeight: 600 }}
                >
                  <TableSortLabel
                    active={sortKey === column.key}
                    direction={
                      sortKey === column.key && descending ? "desc" : "asc"
                    }
                    onClick={() => sortBy(column.key)}
                    sx={sortLabelSx(sortKey === column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((plant) => (
              <TableRow
                key={plant.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/dashboard/plants/${plant.id}`)}
              >
                <TableCell sx={{ fontWeight: 600 }}>{plant.name}</TableCell>
                <TableCell align="right">
                  {mw(plant.capacity_mw) ?? <PendingValue />}
                </TableCell>
                <TableCell align="right">
                  {plant.spof_count ?? <PendingValue />}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: RISK_LEVEL_COLORS.Severe }}
                >
                  {plant.severe_count}
                </TableCell>
                <TableCell align="right" sx={{ color: RISK_LEVEL_COLORS.High }}>
                  {plant.high_count}
                </TableCell>
                <TableCell align="right">{plant.medium_count}</TableCell>
                <TableCell align="right">{plant.low_count}</TableCell>
                <TableCell align="right">
                  {mw(plant.exposed_mw) ?? <PendingValue />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </DashboardCard>
  );
}
